// src/lib/components/atoms/UnitValueInput/UnitValueInputAtom.tsx
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useTranslation } from 'localization';
import type { UnitValue, UnitConfig } from 'types/schema';
import './UnitValueInputAtom.scss';

/**
 * Conversion tables + math come from @center-platform/units — the single source
 * of truth shared with backend validation, covering all runtime categories
 * (time, distance, speed, mass, volume, temperature, area) with i18n label keys
 * per unit. This atom previously kept its own TIME_UNITS table, which drifted
 * from the server's copy.
 */
import {
  toCanonical as sharedToCanonical,
  fromCanonical as sharedFromCanonical,
  getCanonicalUnit,
  getCategoryUnits,
  getUnitDefinition,
  normalizeUnit,
} from '@center-platform/units';

export interface UnitValueInputAtomProps {
  /** Current value - can be number, partial UnitValue, or full UnitValue */
  value: number | Partial<UnitValue> | UnitValue | null;
  /** Called with the new UnitValue whenever it changes */
  onChange: (value: UnitValue | null) => void;
  /** Unit configuration from schema property */
  unitConfig: UnitConfig;
  /** Disable the input */
  disabled?: boolean;
  /** Invalid state styling */
  invalid?: boolean;
  /** Minimum value constraint (from property rules) */
  min?: number;
  /** Maximum value constraint (from property rules) */
  max?: number;
  /** Step increment for steppers (default: 1) */
  step?: number;
  /** Force integer values only (from property rules) */
  integer?: boolean;
  /** Maximum decimal places (overrides unitConfig.displayPrecision) */
  precision?: number;
  /** Show stepper buttons (default: true) */
  showStepper?: boolean;
  /** Additional class name */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  /** Placeholder text */
  placeholder?: string;
  /** Accessible name for the numeric input — this atom renders no visible `<label>` of its own. */
  ariaLabel?: string;
}

/**
 * Check if value is a complete UnitValue object
 */
function isUnitValue(value: unknown): value is UnitValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'unit' in value &&
    'canonicalValue' in value &&
    'canonicalUnit' in value
  );
}

/**
 * Render-safe conversion wrappers: the shared toCanonical/fromCanonical throw for
 * an unknown unit within a supported category (correct server-side, fail-closed),
 * but a stored value with a bad unit must not crash the input — fall back to the
 * value unchanged and let backend validation surface the problem.
 */
function toCanonical(value: number, unit: string, category: string): number {
  try {
    return sharedToCanonical(value, unit, category);
  } catch {
    return value;
  }
}

function fromCanonical(canonicalValue: number, targetUnit: string, category: string): number {
  try {
    return sharedFromCanonical(canonicalValue, targetUnit, category);
  } catch {
    return canonicalValue;
  }
}

/**
 * Create a UnitValue object
 */
function createUnitValue(
  value: number,
  unit: string,
  category: string,
  precision?: number
): UnitValue {
  const canonicalUnit = getCanonicalUnit(category);
  const canonicalValue = toCanonical(value, unit, category);

  return {
    value,
    unit,
    canonicalValue,
    canonicalUnit,
    precision,
  };
}

/**
 * UnitValueInputAtom - Input component for unit-aware numeric values
 *
 * Integrated input with inline unit suffix, dropdown trigger, and optional stacked steppers.
 */
export const UnitValueInputAtom: React.FC<UnitValueInputAtomProps> = ({
  value,
  onChange,
  unitConfig,
  disabled = false,
  invalid = false,
  min,
  max,
  step = 1,
  integer = false,
  precision,
  showStepper = true,
  className,
  style,
  placeholder,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const { category, allowedUnits, defaultUnit, showSwitcher = true, displayPrecision = 2 } = unitConfig;
  const overlayRef = useRef<OverlayPanel>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Effective precision: 0 for integers, precision prop takes precedence, then displayPrecision
  const effectivePrecision = integer ? 0 : (precision ?? displayPrecision);

  // Get available unit options with translated labels. allowedUnits entries are
  // normalized (aliases like 'hr' resolve to 'h') before lookup; unknown units drop.
  const unitOptions = useMemo(() => {
    const units = allowedUnits
      ? allowedUnits.map((u) => normalizeUnit(u, category))
      : getCategoryUnits(category);
    return units
      .map((u) => ({ symbol: u, def: getUnitDefinition(u, category) }))
      .filter((entry) => entry.def)
      .map(({ symbol, def }) => ({
        value: symbol,
        label: t(def!.labelKey) || symbol,
        abbrev: t(def!.abbrevKey) || symbol,
      }));
  }, [allowedUnits, category, t]);

  // Parse current value
  const { numericValue, currentUnit } = useMemo(() => {
    if (value === null || value === undefined) {
      return { numericValue: null, currentUnit: defaultUnit };
    }

    if (typeof value === 'number') {
      return { numericValue: value, currentUnit: defaultUnit };
    }

    if (isUnitValue(value)) {
      return { numericValue: value.value, currentUnit: value.unit };
    }

    // Partial UnitValue
    if (typeof value === 'object' && 'value' in value) {
      return {
        numericValue: value.value ?? null,
        currentUnit: value.unit || defaultUnit,
      };
    }

    return { numericValue: null, currentUnit: defaultUnit };
  }, [value, defaultUnit]);

  // Get current unit info with translated labels
  const currentUnitInfo = useMemo(() => {
    const unitDef = getUnitDefinition(currentUnit, category);
    if (unitDef) {
      return {
        label: t(unitDef.labelKey),
        abbrev: t(unitDef.abbrevKey),
      };
    }
    return { label: currentUnit, abbrev: currentUnit };
  }, [currentUnit, category, t]);

  // Clamp value to min/max constraints
  const clampValue = useCallback((val: number): number => {
    let result = val;
    if (integer) {
      result = Math.round(result);
    }
    if (min !== undefined && result < min) {
      result = min;
    }
    if (max !== undefined && result > max) {
      result = max;
    }
    return result;
  }, [min, max, integer]);

  // Handle numeric value change
  const handleValueChange = useCallback(
    (e: InputNumberValueChangeEvent) => {
      const newValue = e.value;

      if (newValue === null || newValue === undefined) {
        onChange(null);
        return;
      }

      const clampedValue = clampValue(newValue);
      const unitValue = createUnitValue(clampedValue, currentUnit, category, effectivePrecision);
      onChange(unitValue);
    },
    [currentUnit, category, effectivePrecision, onChange, clampValue]
  );

  // Handle stepper increment
  const handleIncrement = useCallback(() => {
    if (disabled) return;

    const currentVal = numericValue ?? 0;
    let newValue = currentVal + step;

    if (integer) {
      newValue = Math.round(newValue);
    }

    // Check max constraint
    if (max !== undefined && newValue > max) {
      newValue = max;
    }

    const unitValue = createUnitValue(newValue, currentUnit, category, effectivePrecision);
    onChange(unitValue);
  }, [numericValue, step, integer, max, currentUnit, category, effectivePrecision, onChange, disabled]);

  // Handle stepper decrement
  const handleDecrement = useCallback(() => {
    if (disabled) return;

    const currentVal = numericValue ?? 0;
    let newValue = currentVal - step;

    if (integer) {
      newValue = Math.round(newValue);
    }

    // Check min constraint
    if (min !== undefined && newValue < min) {
      newValue = min;
    }

    const unitValue = createUnitValue(newValue, currentUnit, category, effectivePrecision);
    onChange(unitValue);
  }, [numericValue, step, integer, min, currentUnit, category, effectivePrecision, onChange, disabled]);

  // Handle unit change - convert existing value to new unit
  // IMPORTANT: Preserve the original canonical value to avoid precision loss
  const handleUnitSelect = useCallback(
    (newUnit: string) => {
      overlayRef.current?.hide();
      setIsOpen(false);

      if (numericValue === null) {
        // No value yet, just update the unit preference
        onChange(createUnitValue(0, newUnit, category, effectivePrecision));
        return;
      }

      // Get the original canonical value - preserve this to avoid precision loss!
      // If we have a full UnitValue, use its stored canonicalValue
      // Otherwise, calculate from current displayed value
      let originalCanonical: number;
      if (isUnitValue(value)) {
        originalCanonical = value.canonicalValue;
      } else {
        originalCanonical = toCanonical(numericValue, currentUnit, category);
      }

      // Convert canonical to display value in new unit
      const convertedValue = fromCanonical(originalCanonical, newUnit, category);

      // Round for display only
      const roundedValue = Math.round(convertedValue * Math.pow(10, effectivePrecision)) / Math.pow(10, effectivePrecision);

      // Create UnitValue preserving the original canonical value
      // This ensures switching back to original unit returns the exact original value
      const canonicalUnit = getCanonicalUnit(category);
      const unitValue: UnitValue = {
        value: roundedValue,
        unit: newUnit,
        canonicalValue: originalCanonical, // Preserve original!
        canonicalUnit,
        precision: effectivePrecision,
      };
      onChange(unitValue);
    },
    [value, numericValue, currentUnit, category, effectivePrecision, onChange]
  );

  // Toggle overlay
  const handleUnitClick = (e: React.MouseEvent) => {
    if (disabled || !showSwitcher || unitOptions.length <= 1) return;
    overlayRef.current?.toggle(e);
    setIsOpen(!isOpen);
  };

  const canSwitchUnits = showSwitcher && unitOptions.length > 1 && !disabled;

  // Check if steppers should be disabled at boundaries
  const isAtMin = min !== undefined && numericValue !== null && numericValue <= min;
  const isAtMax = max !== undefined && numericValue !== null && numericValue >= max;

  return (
    <div
      className={`unit-value-input ${invalid ? 'unit-value-input--invalid' : ''} ${disabled ? 'unit-value-input--disabled' : ''} ${className || ''}`}
      style={style}
    >
      <div className="unit-value-input__input-area">
        <InputNumber
          value={numericValue}
          onValueChange={handleValueChange}
          mode="decimal"
          minFractionDigits={0}
          maxFractionDigits={effectivePrecision}
          min={min}
          max={max}
          disabled={disabled}
          className="unit-value-input__number"
          placeholder={placeholder}
          aria-label={ariaLabel}
        />

        {showStepper && (
          <div className="unit-value-input__steppers">
            <button
              type="button"
              className={`unit-value-input__stepper unit-value-input__stepper--up ${isAtMax ? 'unit-value-input__stepper--disabled' : ''}`}
              onClick={handleIncrement}
              disabled={disabled || isAtMax}
              tabIndex={-1}
              aria-label="Increase value"
            >
              <i className="pi pi-chevron-up" />
            </button>
            <button
              type="button"
              className={`unit-value-input__stepper unit-value-input__stepper--down ${isAtMin ? 'unit-value-input__stepper--disabled' : ''}`}
              onClick={handleDecrement}
              disabled={disabled || isAtMin}
              tabIndex={-1}
              aria-label="Decrease value"
            >
              <i className="pi pi-chevron-down" />
            </button>
          </div>
        )}
      </div>

      {canSwitchUnits ? (
        <button
          type="button"
          className="unit-value-input__suffix unit-value-input__suffix--clickable"
          onClick={handleUnitClick}
          disabled={disabled}
          title={t('units.changeUnit', { unit: currentUnitInfo.label })}
        >
          <span className="unit-value-input__unit-label">{currentUnitInfo.label}</span>
          <i className={`pi pi-chevron-down unit-value-input__chevron ${isOpen ? 'unit-value-input__chevron--open' : ''}`} />
        </button>
      ) : (
        <span
          className="unit-value-input__suffix"
          title={currentUnitInfo.label}
        >
          <span className="unit-value-input__unit-label">{currentUnitInfo.label}</span>
        </span>
      )}

      <OverlayPanel
        ref={overlayRef}
        className="unit-value-input__overlay"
        onHide={() => setIsOpen(false)}
      >
        <div className="unit-value-input__options">
          {unitOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`unit-value-input__option ${option.value === currentUnit ? 'unit-value-input__option--selected' : ''}`}
              onClick={() => handleUnitSelect(option.value)}
            >
              <span className="unit-value-input__option-label">{option.label}</span>
              <span className="unit-value-input__option-abbrev">({option.abbrev})</span>
            </button>
          ))}
        </div>
      </OverlayPanel>
    </div>
  );
};

export default UnitValueInputAtom;
